import React, { useMemo, useEffect, Suspense } from "react";
import { Box, Heading, Text, Spinner } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchPropertyList } from "../../../api/Property/propertyApi";
import Carousel from "../../common/Carousal";
import { generateSlug } from "../../../utils/Helper";
import { FALLBACK_IMAGE } from "../../../constants/constant";
import { bucket_base_url } from "../../../constants/constant";
import { imageCache } from "../../../utils/constant";
import { FormattedMessage } from "react-intl";
import LoadingSpinner from "../../../components/common/Spinner";

const PropertyGrid = ({ selectedType, isCompletionStatus }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = useMemo(
    () => ({
      property_type: isCompletionStatus ? null : selectedType || "OF",
      completion_status: isCompletionStatus ? "off_plan" : null,
    }),
    [selectedType, isCompletionStatus]
  );

  const {
    data: propertyList,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ["PropertyList", queryParams],
    queryFn: () => fetchPropertyList(queryParams),
    select: (res) => res?.data,
    placeholderData: (previousData) => previousData,
  });

  const filteredProperties = useMemo(
    () => propertyList?.records?.slice(0, 50) || [],
    [propertyList]
  );

  // Preload the first image of every property into the in-memory cache
  // so PropertyCard and ImageCarousel can render them instantly on next visit.
  useEffect(() => {
    filteredProperties.forEach((property) => {
      const raw = property?.file?.[0]?.filePath;
      if (!raw) return;
      const url = raw.includes("http") ? raw : `${bucket_base_url}${raw}`;
      if (!imageCache.has(url)) {
        const img = new window.Image();
        img.src = url;
        img.onload = () => imageCache.set(url, true);
        img.onerror = () => imageCache.set(url, true);
      }
    });
  }, [filteredProperties]);

  if (isLoading) {
    return <LoadingSpinner />;
  }


  if (isError) {
    return (
      <Text>
        <FormattedMessage
          id="error_loading_properties"
          defaultMessage="Error loading properties: {message}"
          values={{ message: error?.message }}
        />
      </Text>
    );
  }

  if (!filteredProperties || filteredProperties?.length === 0) {
    return (
      <Text>
        <FormattedMessage
          id="no_properties_found_filter"
          defaultMessage="No properties found for the selected filter."
        />
      </Text>
    );
  }


  const handlePropertyClick = (property) => {
    // Save current section to Home page state before leaving
    navigate(location.pathname + location.search, {
      replace: true,
      state: { fromSection: "explore-apartments-section" },
    });
    // Then navigate to details
    navigate(
      `/property-details/${property.id}-${generateSlug(property.title_en)}`
    );
  };

  return (
    <Box position="relative">
      {isFetching && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          backgroundColor="rgba(245, 245, 245, 0.7)"
          zIndex={10}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="15px"
        >
          <LoadingSpinner />
        </Box>
      )}
      <Suspense fallback={<Spinner size="xl" />}>
        <Carousel items={filteredProperties}>
          {filteredProperties?.map((property) => (
            <Box
              key={property?.id}
              borderWidth="1px"
              borderRadius="15px"
              overflow="hidden"
              boxShadow="lg"
              backgroundColor="white"
              height={selectedType === "OF" ? "400px" : "420px"}
              mr={{ base: 0, md: 0 }}
              width={{ base: "85%", custom: "97%", md: "350px" }}
              mx={{ base: "auto", md: "0" }}
              onClick={() => handlePropertyClick(property)}
              cursor="pointer"
            >
              {
                (() => {
                  const raw = property?.file?.[0]?.filePath;
                  const src = raw
                    ? raw.includes("http")
                      ? raw
                      : `${bucket_base_url}${raw}`
                    : FALLBACK_IMAGE;
                  // Use fallback immediately if not yet cached to avoid Chakra's fallback flicker
                  const resolved = imageCache.has(src) ? src : (raw ? src : FALLBACK_IMAGE);
                  return (
                    <img
                      src={resolved}
                      alt={property?.title_en}
                      onLoad={() => imageCache.set(src, true)}
                      onError={(e) => {
                        imageCache.set(src, true);
                        e.target.onerror = null;
                        e.target.src = FALLBACK_IMAGE;
                      }}
                      style={{
                        objectFit: "cover",
                        height: "200px",
                        width: "100%",
                        display: "block",
                      }}
                    />
                  );
                })()
              }
              <Box p="6">
                <Heading as="h3" size="md" mb="2">
                  {property?.title_en}
                </Heading>
                <Text color="gray.500">Location: {property?.community}</Text>
                <Text color="gray.500">Price: AED {property?.price}</Text>
                {selectedType !== "OF" && (
                  <Text color="gray.500">Bedrooms: {property?.bedroom}</Text>
                )}
                <Text color="gray.500">Bathrooms: {property?.bathroom}</Text>
                <Text color="gray.500">Size: {property?.size} sqft</Text>
              </Box>
            </Box>
          ))}
        </Carousel>
      </Suspense>
    </Box>
  );
};

export default PropertyGrid;
